import { action, observable, runInAction, makeObservable } from 'mobx';
import { UserEditForm } from 'modules/user/forms';
import { BaseViewStore } from 'core/stores';
import { formInvalidate, notificationError, generateRandomString, isSuperAdminOrganization } from 'core/utils';
import { roles as allRoles, sellerRoles, warehouseRoles } from 'common/constants';
import { PrintNodeInfoStore } from 'modules/print-node-info/stores';
import { CoreUserInfoStore } from 'modules/core-user-info/stores';
import { lookupRouteName } from 'common/constants';
import { LookupStore } from 'modules/lookup/stores';
import { FulfillmentUserStore } from 'modules/membership/stores';
import _ from 'lodash';
import { ApplicationStore } from 'platform/application/stores';
import { CoreUserService } from 'common/data';

class UserEditViewStore extends BaseViewStore {
	@observable loadingRoles = false;
	@observable showNotification = false;
	@observable selectedLabelPrinter = null;
	@observable selectedPackingSlipPrinter = null;
	@observable enableEmailNotification = null;
	@observable canSetEnableEmailNotification = false;
	@observable hasPiiAccess = null;
	@observable canSetPii = false;
	@observable printerOptions = [];
	@observable selectedRoles = [];
	@observable userRoles = [];
	loggedUser = null;
	notification = null;
	user;
	userInOrganization;
	@observable userPhone = null;
	@observable isHavingAPISecret = false;

	@observable countryOptions = [];
	@observable isCountryLoading = false;
	initialPiiAccess = false;
	timeouts = [];
	isEditMode = true;
	isSuperAdmin = false;
	form = new UserEditForm({
		onSuccess: async () => {
			const userPhone = this.form.$('userPhone').value;
			if (!this.checkUserPhone() && userPhone) {
				this.form.$('userPhone').invalidate('LOGINFORM.INVALIDUSERPHONEFORMAT');
			} else {
				await this.updateUser();
			}
		},
		onError: async () => {
			const userPhone = this.form.$('userPhone').value;
			if (!this.checkUserPhone() && userPhone) {
				this.form.$('userPhone').invalidate('LOGINFORM.INVALIDUSERPHONEFORMAT');
			}
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.loggedUser = rootStore.authStore.user;
		this.parentView = rootStore.userListViewStore;
		this.listStore = rootStore.userListViewStore.listStore;
		this.applicationStore = new ApplicationStore(rootStore);
		this.printNodeInfoStore = new PrintNodeInfoStore(rootStore);
		this.coreUserInfoStore = new CoreUserInfoStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.fulfillmentUserStore = new FulfillmentUserStore(rootStore);
		this.notification = 'LOGINFORM.PRINTNODE.NOTIFICATIONS.REQUIRED';
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.userService = new CoreUserService(rootStore.app.baasic.apiClient);
		this.initialize();
	}

	@action
	initialize = async () => {
		this.suspend();
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		this.form.reset();

		await this.getUser(this.rootStore.routerStore.routerState.queryParams.editId);
		this.user = this.listStore.item;

		await Promise.all([
			this.getPrintNodeInfo(this.user.id),
			this.getCoreUserInfoAndCheckPermission(this.user.id),
			this.fetchRoles(),
			this.getCountryLookupOptions(),
		]);

		const { roles, ...other } = this.listStore.item;
		this.form.update(other);
		this.form.$('roles').set(
			_.map(roles, role => {
				return { value: role.id, label: role.name };
			})
		);
		this.isMainAccountOwner =
			_.findIndex(
				roles,
				role => role.name === allRoles.warehouseAccountOwner || role.name === allRoles.sellerAccountOwner
			) != -1 && this.user.isMainOwner;
		if ((this.user.id != this.rootStore.authStore.user.id && this.hasPiiAccess) || this.isSuperAdmin) {
			this.canSetPii = true;
		} else {
			this.canSetPii = false;
		}

		const loggedUserRole = _.find(this.loggedUser.roles, role => role == warehouseRoles.warehouseAccountOwner);
		if (loggedUserRole) {
			this.canSetEnableEmailNotification = true;
		} else {
			this.canSetEnableEmailNotification = false;
		}
		this.resume();
	};

	@action
	getUser = async id => {
		await this.listStore.getUser(id);
	};

	@action
	getCoreUserInfoAndCheckPermission = async userId => {
		await this.coreUserInfoStore.findCoreUserInfo({ userId: userId, embed: ['coreCountry'] });
		if (this.coreUserInfoStore.response.totalRecords != 0) {
			const coreUserInfo = this.coreUserInfoStore.response.item[0];
			this.hasPiiAccess = coreUserInfo.piiAccess;
			this.initialPiiAccess = coreUserInfo.piiAccess;
			if (coreUserInfo.coreCountry) {
				this.form.$('countryCode').value = {
					value: coreUserInfo.coreCountry.id,
					label: `${coreUserInfo.coreCountry.phoneCode} | ${coreUserInfo.coreCountry.name}`,
					extra: coreUserInfo.coreCountry,
				};
			}

			let arr = coreUserInfo.userPhone ? coreUserInfo.userPhone.split(' ') : [];
			if (arr.length > 1) {
				arr.shift();
			}
			this.form.$('userPhone').set('value', arr.join(''));
			if (coreUserInfo.apiSecret != '') {
				this.isHavingAPISecret = true;
				this.form.$('apiSecret').set('value', coreUserInfo.apiSecret);
			}
			this.enableEmailNotification = coreUserInfo.enableEmailNotification;
		} else {
			this.hasPiiAccess = false;
		}
	};

	@action
	getPrintNodeInfo = async userId => {
		await this.printNodeInfoStore.findPrintNodeInfo({ userId: userId });
		if (this.printNodeInfoStore.response.totalRecords > 0) {
			const printNodeInfo = this.printNodeInfoStore.response.item[0];
			if (printNodeInfo.apiKey != '') {
				this.form.$('apiKey').value = printNodeInfo.apiKey;
			}
			if (printNodeInfo.labelPrinterId != '' && printNodeInfo.labelPrinterName != '') {
				this.form.$('labelPrinterId').value = printNodeInfo.labelPrinterId;
				this.form.$('labelPrinterName').value = printNodeInfo.labelPrinterName;
				this.selectedLabelPrinter = { label: printNodeInfo.labelPrinterName, value: printNodeInfo.labelPrinterId };
			}
			if (printNodeInfo.packingSlipPrinterId != '' && printNodeInfo.packingSlipPrinterName != '') {
				this.form.$('packingSlipPrinterId').value = printNodeInfo.packingSlipPrinterId;
				this.form.$('packingSlipPrinterName').value = printNodeInfo.packingSlipPrinterName;
				this.selectedPackingSlipPrinter = {
					label: printNodeInfo.packingSlipPrinterName,
					value: printNodeInfo.packingSlipPrinterId,
				};
			}
			if (printNodeInfo.apiKey != '') {
				try {
					await this.printNodeInfoStore.getPrinters(printNodeInfo.apiKey);
					_.forEach(this.printNodeInfoStore.response.printers, item => {
						this.printerOptions.push({ value: item.id, label: item.name });
					});
				} catch (err) {
					if (err.data != null && !err.data.isSuccess) {
						this.rootStore.notificationStore.error('LOGINFORM.PRINTNODE.NOTIFICATIONS.INVALIDAPIKEY');
					} else {
						notificationError({ store: this.rootStore.notificationStore, err });
					}
				}
			}
		}
	};

	@action
	fetchRoles = async () => {
		this.loadingRoles = true;
		this.selectedRoles = [];
		this.userRoles = [];
		this.userInOrganization = await this.userService.find({
			userId: this.user.id,
			embed: ['organization', 'organization.organizationType'],
		});
		await this.listStore.fetchRoles(this.userInOrganization.data.item[0].organization.organizationType.name);

		_.forEach(this.listStore.roles, item => {
			this.userRoles.push({ value: item.id, label: item.name });
		});
		_.forEach(this.user.roles, item => {
			this.selectedRoles.push({ value: item.id, label: item.name });
		});

		runInAction(() => (this.loadingRoles = false));
	};

	@action.bound
	async getCountryLookupOptions() {
		await this.lookupStore.find({ page: 1, rpp: 300 }, lookupRouteName.country);
		this.countryOptions = _.map(this.lookupStore.data, item => {
			return { label: `${item.name} | ${item.phoneCode}`, value: item.id, extra: item };
		});
	}

	@action
	updateUser = async () => {
		this.suspend();
		const isEmailValid = await this.checkEmail();
		if (!isEmailValid) {
			this.form.invalidate('USEREDIT.INVALIDEMAIL');
		} else {
			Object.assign(this.user, this.form.values());
			this.user.roles = _.map(this.selectedRoles, role => {
				return { name: role.label, id: role.value };
			});
			if (!this.canSetPii) {
				this.hasPiiAccess = this.initialPiiAccess;
			}
			try {
				let _userId = this.rootStore.routerStore.routerState.queryParams.editId;
				let userObj = {
					id: _userId,
					piiAccess: this.hasPiiAccess,
					apiSecret: this.user.apiSecret,
					enableEmailNotification: this.enableEmailNotification,
					countryId: this.form.$('countryCode').value.value,
					...this.user,
					userPhone: this.form.$('countryCode').value.extra.phoneCode + ' ' + this.form.$('userPhone').value,
				};

				delete userObj.countryCode;
				await this.listStore.updateUser(userObj);
				this.rootStore.notificationStore.success(['LOOKUPUPDATE.USER', { user: this.user.userName.split('-')[0] }]);
				await this.parentView.findUsers();
				this.parentView.closeModal();
			} catch (err) {
				formInvalidate({ form: this.form, err });
			}
		}
		this.resume();
	};

	@action.bound
	checkEmail = async () => {
		const { email } = this.form.values();
		if (email.trim() !== this.user.email.trim()) {
			const response = await this.fulfillmentUserStore.validateEmail(email);
			return response.data;
		}
		return true;
	};

	@action.bound
	getPrinters = async () => {
		try {
			const { apiKey } = this.form.values();
			if (apiKey.trim() == '') {
				this.showNotification = true;
			} else {
				this.showNotification = false;
				this.clearPrinters();
				await this.printNodeInfoStore.getPrinters(apiKey);
				if (this.printNodeInfoStore.response.printers.length > 0) {
					_.forEach(this.printNodeInfoStore.response.printers, item => {
						this.printerOptions.push({ value: item.id, label: item.name });
					});
					this.rootStore.notificationStore.success('LOGINFORM.PRINTNODE.NOTIFICATIONS.PRINTERSFOUND');
				} else {
					this.rootStore.notificationStore.success('LOGINFORM.PRINTNODE.NOTIFICATIONS.PRINTERSNOTFOUND');
				}
			}
		} catch (err) {
			if (err.data != null && !err.data.isSuccess) {
				this.rootStore.notificationStore.error('LOGINFORM.PRINTNODE.NOTIFICATIONS.INVALIDAPIKEY');
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
	};

	@action.bound
	clearPrinters = () => {
		this.printerOptions = [];
		this.selectedLabelPrinter = null;
		this.form.$('labelPrinterId').value = '';
		this.form.$('labelPrinterName').clear();
		this.selectedPackingSlipPrinter = null;
		this.form.$('packingSlipPrinterId').value = '';
		this.form.$('packingSlipPrinterName').clear();
	};

	onChangeLabelPrinter = selectedValue => {
		this.selectedLabelPrinter = selectedValue;
		if (selectedValue != null) {
			this.form.$('labelPrinterId').value = selectedValue.value;
			this.form.$('labelPrinterName').value = selectedValue.label;
		} else {
			this.form.$('labelPrinterId').value = '';
			this.form.$('labelPrinterName').clear();
		}
	};

	onChangePackingSlipPrinter = selectedValue => {
		this.selectedPackingSlipPrinter = selectedValue;
		if (selectedValue != null) {
			this.form.$('packingSlipPrinterId').value = selectedValue.value;
			this.form.$('packingSlipPrinterName').value = selectedValue.label;
		} else {
			this.form.$('packingSlipPrinterId').value = '';
			this.form.$('packingSlipPrinterName').clear();
		}
	};

	@action.bound
	onCheckPiiAccess = props => {
		if (props) {
			this.hasPiiAccess = props.currentTarget.checked;
		}
	};

	@action.bound
	onCheckEnableEmailNotification = props => {
		if (props) {
			this.enableEmailNotification = props.currentTarget.checked;
		}
	};

	onRoleChange = selectedValue => {
		this.selectedRoles = selectedValue == null ? [] : selectedValue;
		this.form.$('roles').value = this.selectedRoles;
	};

	@action.bound
	onSelectChange = (value, field) => {
		this.form.$(field.path).set('value', value);
	};

	onUserPhoneChange = value => {
		// Automatically correct user phone input 2.5 seconds after user stops typing
		const highestTimeoutId = setTimeout(() => {
			this.form.$('userPhone').resetValidation();
			this.timeouts.forEach(timeoutId => {
				if (timeoutId < highestTimeoutId) {
					clearTimeout(timeoutId);
				}
			});
			this.timeouts.push(
				setTimeout(() => {
					this.correctUserPhone(value);
				}, 2500)
			);
		}, 0);
	};

	@action.bound
	correctUserPhone = () => {
		const regex = /^\d+$/;
		var value = this.form.$('userPhone').value;
		if (!value.match(regex) && value) {
			value = value.replace(/\D/g, '');
			this.form.$('userPhone').value = value;
		}
	};

	@action.bound
	checkUserPhone = () => {
		let value = this.form.$('userPhone').value;
		if (value) {
			const regex = /^\d+$/;
			var phone = this.form.$('countryCode').value.extra.phoneCode + value;
			if ((!value.match(regex) && value.length > 0) || (phone.length < 7 || phone.length > 15)) {
				return false;
			} else {
				return true;
			}
		}
	};

	@action.bound generateApiSecret() {
		this.isHavingAPISecret = true;
		var radnomStringGenerated = generateRandomString(32);
		this.form.$('apiSecret').set('value', radnomStringGenerated);
	}

	@action.bound
	async onMenuClose() {
		await this.getCountryLookupOptions();
	}
}

export default UserEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/user/stores/UserEditViewStore.js